// =============================================================================
// App.tsx — Root application component
// Project: Texas Hold'em Social Casino
// =============================================================================
// Manages top-level screen routing and platform SDK initialization.
// On mount, checks for saved auth token and auto-login if available.

import React, { useEffect } from 'react'
import { usePlayerStore } from './store/player-store'
import { useUIStore } from './store/ui-store'
import { createPlatformAdapter, PlatformAdapter } from './platform/interface'

// Screen imports
import LoadingScreen from './game/LoadingScreen'
import LoginScreen from './auth/LoginScreen'
import LobbyScreen from './lobby/Lobby'
import TablesScreen from './lobby/Tables'
import PokerTable from './game/PokerTable'
import ShopScreen from './shop/Shop'
import LeaderboardScreen from './lobby/Leaderboard'
import MissionsScreen from './progression/Missions'
import ClubsScreen from './social/Clubs'
import FriendsScreen from './social/Friends'
import ProfileScreen from './auth/Profile'
import ToastContainer from './game/ToastContainer'

// Global styles
import './styles.css'

export let platformAdapter: PlatformAdapter

export default function App(): React.ReactElement {
  const { token, restoreToken } = usePlayerStore()
  const { currentScreen, navigateTo } = useUIStore()
  const [platformReady, setPlatformReady] = React.useState(false)

  useEffect(() => {
    async function init(): Promise<void> {
      console.log('[App] Initializing...')
      platformAdapter = createPlatformAdapter()
      if (!platformAdapter.isStandalone() && 'init' in platformAdapter) {
        await (platformAdapter as any).init()
      }

      setPlatformReady(true)

      const savedToken = localStorage.getItem('poker_token')
      if (savedToken) {
        console.log('[App] Found saved token, restoring session')
        navigateTo('loading')
        try {
          await restoreToken(savedToken)
          console.log('[App] Session restored')
          navigateTo('lobby')
        } catch {
          console.log('[App] Session restore failed, showing login')
          navigateTo('login')
        }
      } else {
        console.log('[App] No saved token, showing login')
        navigateTo('login')
      }
    }

    init()
  }, [])

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = (): void => {
      useUIStore.getState().setIsMobile(window.innerWidth < 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const renderScreen = (): React.ReactElement => {
    switch (currentScreen) {
      case 'loading':
        return <LoadingScreen />
      case 'login':
        return <LoginScreen />
      case 'lobby':
        return <LobbyScreen />
      case 'tables':
        return <TablesScreen />
      case 'table':
        return <PokerTable />
      case 'shop':
        return <ShopScreen />
      case 'leaderboard':
        return <LeaderboardScreen />
      case 'missions':
        return <MissionsScreen />
      case 'clubs':
        return <ClubsScreen />
      case 'friends':
        return <FriendsScreen />
      case 'profile':
        return <ProfileScreen />
      default:
        return <LoginScreen />
    }
  }

  return (
    <div className="app">
      {renderScreen()}
      <ToastContainer />
    </div>
  )
}
